import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ROOT_MIDI,
  TRAIL,
  getPlace,
  getRegion,
  loadProgress,
  saveProgress,
  withVisit,
  withPhrase,
  placeState,
  emptyPhrase,
  phraseHasNotes,
  trailIndexOf,
  isEchoDue,
  briefFor,
} from "../lib/scaleCountry";
import {
  audioSupported,
  playPhraseSteps,
  playCharDemo,
} from "../lib/scaleCountryAudio";
import ScaleKeyboard from "../components/scaleCountry/ScaleKeyboard";
import DroneButton from "../components/scaleCountry/DroneButton";
import PhraseGrid from "../components/scaleCountry/PhraseGrid";

const TABS = ["LISTEN", "EXPLORE", "LEARN", "WRITE"];

/* ============================================================
   /scale-country/:placeId — a single place. Visiting stamps
   "visited" immediately; leaving a phrase on the WRITE tab
   stamps it "stamped". Both write straight through to the
   shared progress blob in localStorage.
   ============================================================ */

const ScaleCountryPlace = () => {
  const { placeId } = useParams();
  const place = getPlace(placeId);
  const [progress, setProgress] = useState(loadProgress);
  const [tab, setTab] = useState("EXPLORE");
  const [phrase, setPhrase] = useState(
    () => (progress.phrases && progress.phrases[placeId]) || emptyPhrase()
  );
  const [justStamped, setJustStamped] = useState(false);

  useEffect(() => {
    if (!getPlace(placeId)) return;
    const next = withVisit(loadProgress(), placeId);
    saveProgress(next);
    setProgress(next);
    setPhrase((next.phrases && next.phrases[placeId]) || emptyPhrase());
    setJustStamped(false);
    setTab("EXPLORE");
  }, [placeId]);

  if (!place) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[#1B221A] text-[#E9EDDC]">
        <p className="font-mono text-sm text-[#97A18B]">
          this place isn't on the map
        </p>
        <Link
          to="/scale-country"
          className="font-mono text-xs text-[#A6C46A] hover:underline"
        >
          ◂ back to the map
        </Link>
      </div>
    );
  }

  const region = getRegion(place.regionId);
  const state = placeState(progress, place.id);
  const echoDue = state === "stamped" && isEchoDue(progress, place.id);
  const trailIdx = trailIndexOf(place.id);
  const onTrail = trailIdx !== -1;
  const trailNext =
    onTrail && trailIdx < TRAIL.length - 1 ? getPlace(TRAIL[trailIdx + 1]) : null;
  const brief = briefFor(progress, place.id);

  const handleSave = () => {
    const next = withPhrase(progress, place.id, phrase);
    saveProgress(next);
    setProgress(next);
    setJustStamped(phraseHasNotes(phrase));
  };

  const hearVibe = () => {
    const steps = place.listenPhrase.map((off) =>
      off == null ? [] : [ROOT_MIDI + off]
    );
    playPhraseSteps(steps, { stepMs: 280 });
  };

  return (
    <div className="min-h-screen bg-[#1B221A] px-4 py-10 text-[#E9EDDC] sm:px-8">
      <div className="mx-auto max-w-3xl">
        {!audioSupported() && (
          <p className="mb-4 font-mono text-xs text-[#79855F]">
            audio isn't available in this browser — the keys will be quiet
          </p>
        )}

        <Link
          to="/scale-country"
          className="font-mono text-xs text-[#79855F] hover:text-[#A6C46A]"
        >
          ◂ back to the map
        </Link>

        <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#79855F]">
              {region?.name} · Scale Country
            </p>
            <h1 className="mt-2 font-serif text-3xl sm:text-4xl">{place.name}</h1>
            <span className="mt-2 inline-block rounded-md border border-[#45523F] px-2 py-0.5 font-mono text-[10px] uppercase tracking-wide text-[#97A18B]">
              {place.moodTag}
            </span>
          </div>
          <span
            className={`rounded-md border px-3 py-1.5 font-mono text-xs uppercase tracking-wide ${
              echoDue
                ? "border-[#E0A458] text-[#E0A458]"
                : state === "stamped"
                ? "border-[#A6C46A] bg-[#A6C46A] font-bold text-[#1B221A]"
                : "border-[#45523F] text-[#97A18B]"
            }`}
          >
            {state}
            {echoDue && " · echo due"}
          </span>
        </div>

        <div className="mt-4 rounded-md border border-dashed border-[#45523F] px-4 py-2 font-mono text-xs uppercase tracking-wide text-[#79855F]">
          {onTrail ? (
            <>
              TRAIL STOP {trailIdx + 1}/{TRAIL.length}
              {trailNext && <> · next: {trailNext.name}</>}
            </>
          ) : (
            "OFF THE TRAIL · free roam"
          )}
        </div>

        <div className="mt-6 flex gap-1 border-b border-[#39432F]">
          {TABS.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setTab(t)}
              className={`px-3 py-2 font-mono text-xs uppercase tracking-wide ${
                tab === t
                  ? "border-b-2 border-[#A6C46A] text-[#A6C46A]"
                  : "text-[#79855F] hover:text-[#E9EDDC]"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        <div className="mt-6 rounded-lg border border-[#39432F] bg-[#222B20] p-5">
          {tab === "LISTEN" && (
            <div>
              <p className="max-w-xl font-serif text-lg leading-relaxed">
                {place.mood}
              </p>
              <p className="mt-3 font-mono text-xs text-[#97A18B]">
                lives in: {place.songRefs.join(" · ")}
              </p>
              <button
                type="button"
                onClick={hearVibe}
                className="mt-4 rounded-md border border-[#45523F] px-4 py-2 font-mono text-xs uppercase tracking-wide hover:border-[#A6C46A] hover:bg-[#A6C46A] hover:text-[#1B221A]"
              >
                ▶ HEAR THE VIBE
              </button>
            </div>
          )}

          {tab === "EXPLORE" && (
            <div>
              <div className="flex flex-wrap items-start gap-8">
                <ScaleKeyboard semis={place.semis} rootMidi={ROOT_MIDI} />
                <DroneButton />
              </div>
              <p className="mt-4 font-mono text-xs text-[#79855F]">
                drone on, then wander — nothing you press is wrong.
              </p>
            </div>
          )}

          {tab === "LEARN" && (
            <div className="flex flex-col gap-4 sm:flex-row">
              <div className="rounded-md border border-[#45523F] px-4 py-3 font-mono text-sm text-[#A6C46A]">
                {place.formula}
              </div>
              <div className="flex-1 border-l-2 border-[#A6C46A] pl-4">
                <p className="text-sm text-[#E9EDDC]">{place.charNote.text}</p>
                <button
                  type="button"
                  onClick={() => playCharDemo(place.charNote)}
                  className="mt-3 rounded-md border border-[#45523F] px-3 py-1.5 font-mono text-xs uppercase tracking-wide hover:border-[#A6C46A]"
                >
                  HEAR IT ▸
                </button>
              </div>
            </div>
          )}

          {tab === "WRITE" && (
            <div>
              <div className="rounded-md border border-dashed border-[#45523F] p-4 text-sm text-[#97A18B]">
                {brief.text}
              </div>
              {brief.sourceId && (
                <button
                  type="button"
                  onClick={() =>
                    playPhraseSteps(brief.sourcePhrase, { stepMs: 280 })
                  }
                  className="mt-3 rounded-md border border-[#45523F] px-3 py-1.5 font-mono text-xs uppercase tracking-wide hover:border-[#A6C46A]"
                >
                  ▶ HEAR YOUR {brief.sourceName} PHRASE
                </button>
              )}
              <div className="mt-4">
                <PhraseGrid semis={place.semis} phrase={phrase} onChange={setPhrase} />
              </div>
              <div className="mt-4 flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleSave}
                  className="rounded-md border border-[#45523F] px-4 py-2 font-mono text-xs font-bold uppercase tracking-wide hover:border-[#A6C46A] hover:bg-[#A6C46A] hover:text-[#1B221A]"
                >
                  SAVE TO THIS PAGE
                </button>
                {justStamped && (
                  <span className="font-mono text-xs uppercase tracking-wide text-[#A6C46A]">
                    stamped ●
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScaleCountryPlace;
